import React from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CommunicationSearchItem } from '../../../hooks/Marketing';
import {
    formatDateTime,
    getChannelOption,
    getCommunicationStatusInfo,
} from './constants';

interface CommunicationResultCardProps {
    data: CommunicationSearchItem;
    onDelete: (id: number) => void;
    showCreateCommunicationButton?: boolean;
}

const CommunicationResultCard = ({ data, onDelete, showCreateCommunicationButton = false }: CommunicationResultCardProps) => {
    const channel = getChannelOption(data.defChannelId);
    const ChannelIcon = channel.icon;

    const {
        isTerminated,
        isWaitingValidation,
        isExtractionInProgress,
        isExtracted,
        isTestOperation,
        isModel,
        remaining,
        isExpired,
        canDelete,
        canFinalize,
        canView,
        canDuplicate,
    } = getCommunicationStatusInfo(data);

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...(isExpired && { borderColor: 'error.main', borderWidth: 2 }),
            }}
        >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <FolderOutlinedIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={600} noWrap title={data.defTitle}>
                            {data.defTitle} (#{data.id})
                        </Typography>
                    </Box>
                    <ChannelIcon fontSize="small" titleAccess={channel.label} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                </Box>

                <Divider />

                <Typography variant="caption" color="text.secondary">
                    Créé le {formatDateTime(data.creationDate)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 24, flexWrap: 'wrap' }}>
                    {isTerminated && (
                        <Chip label="Envoyée" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                    {isExtractionInProgress && (
                        <Chip label="Extraction en cours" size="small" color="warning" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                    {isExtracted && (
                        <Chip label="Extraite" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                    {isTestOperation && (
                        <Chip label="Opération de test" size="small" color="info" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                    {isModel && (
                        <Chip label="Modèle" size="small" color="default" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                    {isWaitingValidation && remaining && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ color: isExpired ? 'error.main' : 'success.main' }} />
                            <Typography variant="caption" sx={{ color: isExpired ? 'error.main' : 'success.main', fontWeight: 600 }}>
                                {remaining.label}
                            </Typography>
                        </Box>
                    )}
                    {!isTerminated && !isExtractionInProgress && !isExtracted && !isTestOperation && !isModel && !isWaitingValidation && (
                        <Chip label="Brouillon" size="small" color="default" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                </Box>

                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        {showCreateCommunicationButton && (
                            <Button size="small" variant="outlined" startIcon={<AddIcon fontSize="small" />}>
                                Créer la campagne
                            </Button>
                        )}
                        {canDelete ? (
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon fontSize="small" />}
                                onClick={() => onDelete(data.id)}
                            >
                                Supprimer
                            </Button>
                        ) : (
                            <span />
                        )}

                        {canView && (
                            <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />}>
                                Visualiser
                            </Button>
                        )}
                        {canFinalize && (
                            <Button size="small" variant="contained">
                                Finaliser
                            </Button>
                        )}
                        {canDuplicate && (
                            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}>
                                Dupliquer
                            </Button>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CommunicationResultCard;
